import React, { useState, useEffect } from "react";
import "./TableView.css";
import { useAuth } from "./AuthProvider";
import SaveButton from "./SaveButton";
import { useSavedRecs } from "../hooks/useSavedRecs";
import { useToggleSave } from "../hooks/useToggleSave";

export default function TableView({
  filteredRecords,
  hasRecommender,
  formatDate,
  getCategoryColor,
  sortField,
  sortDirection,
  handleSort,
}) {
  const { session } = useAuth();
  const { data: savedIds = [] } = useSavedRecs(session?.user.id);
  const toggleSaveMutation = useToggleSave(session?.user.id);
  const [localSaved, setLocalSaved] = useState(new Set());
  const [modalButtons, setModalButtons] = useState([]);


  useEffect(() => {
    setLocalSaved(new Set(savedIds));
  }, [savedIds]); // fixes update loop

  const handleToggle = async (recId) => {
    if (!session) {
      localStorage.setItem("pendingSave", recId);
      window.dispatchEvent(new CustomEvent("showVisitModal", {
        detail: {
          title: "Want to save this rec?",
          message: "Log in to stash this rec ❤️",
          buttons: [
            {
              label: "Log In",
              className: "login-button",
              onClick: () => {
                localStorage.setItem("fromModalRedirect", "true");
                const currentPath = window.location.pathname;
                const redirectPath = currentPath === "/" ? "/browse" : currentPath;
                window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`;
              }
            },
            {
              label: "CREATE ACCOUNT",
              className: "link-button",
              onClick: () => {
                localStorage.setItem("fromModalRedirect", "true");
                const currentPath = window.location.pathname;
                const redirectPath = currentPath === "/" ? "/browse" : currentPath;
                window.location.href = `/login?view=sign_up&redirect=${encodeURIComponent(redirectPath)}`;
              }
            }
          ]
        }
      }));
      
      return;
    }

    const isSaved = localSaved.has(recId);
    const newSet = new Set(localSaved);

    if (isSaved) {
      newSet.delete(recId);
    } else {
      newSet.add(recId);
    }

    setLocalSaved(newSet);

    const { error } = await toggleSaveMutation.mutateAsync({ recId, isSaved });

    if (error) {
      // rollback on error
      if (isSaved) {
        newSet.add(recId);
      } else {
        newSet.delete(recId);
      }
      setLocalSaved(new Set(newSet));
    }
  };

  const tableHeaders = [
    "Recommendation",
    "Link",
    "Category",
    ...(hasRecommender ? ["Recommender"] : []),
    "Episode Title",
    "Date",
  ];

  return (
    <table className="airtable-view">
      <thead>
        <tr>
          {tableHeaders.map((header) => (
            <th
              key={header}
              onClick={header === "Date" ? () => handleSort(header) : undefined}
              style={{
                cursor: header === "Date" ? "pointer" : "default",
                userSelect: "none",
                color: header === "Date" && sortField === "Date" ? "#000" : "#333",
              }}
            >
              {header}
              {header === "Date" && sortField === "Date" && (
                <span style={{ marginLeft: "6px" }}>
                  {sortDirection === "asc" ? "▲" : "▼"}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {filteredRecords.map((record) => {
          const recId = record.fields.id || record.id;
          const isSaved = localSaved.has(recId);

          return (
            <tr key={record.id}>
              <td>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                  <SaveButton
                    isSaved={isSaved}
                    onToggle={() => handleToggle(recId)}
                    tableMode
                  />
                  {record.fields?.Recommendation || ""}
                </span>
              </td>
              <td>
                {record.fields?.Link ? (
                  <a
                    href={record.fields.Link}
                    className="card-pill-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Link
                  </a>
                ) : (
                  ""
                )}
              </td>
              <td>
                {(record.fields?.Category || []).map((cat, idx) => (
                  <span
                    key={idx}
                    className="category-pill"
                    style={{ backgroundColor: getCategoryColor(cat) }}
                  >
                    {cat}
                  </span>
                ))}
              </td>
              {hasRecommender && (
                <td>
                  {(record.fields?.Recommender || []).map((rec, idx) => (
                    <span
                      key={idx}
                      className="category-pill"
                      style={{ backgroundColor: getCategoryColor(rec) }}
                    >
                      {rec}
                    </span>
                  ))}
                </td>
              )}
              <td>{record.fields?.Title || "Untitled"}</td>
              <td>{formatDate(record.fields?.Date)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
